import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStores, getLocations, createLocation, updateLocation, deleteLocation } from '../../services/api';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import { Plus, Warehouse, ArrowLeft, Pencil, Trash2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const empty = { name: '', description: '' };

const StoreDetail = () => {
  const { storeId } = useParams();
  const [store, setStore] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const refresh = async () => {
    const [all, locs] = await Promise.all([getStores(), getLocations(storeId)]);
    setStore(all.find(s => s.id === storeId) || null);
    setLocations(locs);
  };

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [storeId]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (loc) => { setEditing(loc); setForm({ name: loc.name, description: loc.description || '' }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Location name required');
    try {
      if (editing) {
        await updateLocation(editing.id, form);
        toast.success('Location updated');
      } else {
        await createLocation(storeId, form);
        toast.success('Location created');
      }
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (loc) => {
    if (!window.confirm(`Delete ${loc.name}? Any zero-stock rows will be cleared.`)) return;
    try {
      await deleteLocation(loc.id);
      toast.success('Location deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading...</div>;
  if (!store) return <div className="p-8 text-slate-500">Store not found. <Link to="/admin/stores" className="text-blue-700">Back</Link></div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <Link to="/admin/stores" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> All stores
      </Link>

      <PageHeader
        title={store.name}
        description={`${store.address || 'No address'} · Manager: ${store.manager || '—'}`}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="new-location-btn">
                <Plus className="h-4 w-4 mr-2" /> New Location
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? 'Edit Location' : 'Create Location'}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div><Label>Name</Label><Input data-testid="location-name-input" className="mt-1.5" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Aisle A - Bin 01, Van #4" /></div>
                <div><Label>Description</Label><Input data-testid="location-desc-input" className="mt-1.5" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional note" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} className="bg-slate-900 hover:bg-slate-800" data-testid="save-location-btn">{editing ? 'Save changes' : 'Create location'}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2 text-sm text-slate-600">
          <Warehouse className="h-4 w-4" /> {locations.length} location{locations.length === 1 ? '' : 's'} in {store.name}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium text-slate-600">ID</th>
              <th className="px-4 py-3 font-medium text-slate-600">Name</th>
              <th className="px-4 py-3 font-medium text-slate-600">Description</th>
              <th className="px-4 py-3 font-medium text-slate-600 w-32 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {locations.map(l => (
              <tr key={l.id} data-testid={`location-row-${l.id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{l.id}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-medium text-slate-900">
                    <MapPin className="h-4 w-4 text-slate-500" /> {l.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{l.description || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(l)} data-testid={`edit-location-${l.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(l)} data-testid={`delete-location-${l.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
            {locations.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">No locations yet. Add bins, shelves, or vans to organise stock.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StoreDetail;
